import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { AppLoading } from "Common/Components/AppLoading/AppLoading.tsx";
import { msalInstance } from "Auth/authConfig.ts";
import { ActivityLogin } from "Services/ActivityLog/ActivityLogin.ts";
import { EventType, type AuthenticationResult, type EventMessage, type AccountInfo } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import App from "./App";

msalInstance.initialize().then(async () => {
  let pendingActivityLogin = false;

  // Register BEFORE handleRedirectPromise — events fire during redirect processing
  msalInstance.addEventCallback(async (event: EventMessage) => {
    // MSAL v5: LOGIN_SUCCESS payload is AccountInfo, not AuthenticationResult
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      const account = event.payload as AccountInfo;
      msalInstance.setActiveAccount(account);
      localStorage.removeItem("isUserLoggedOut");
      pendingActivityLogin = true;
      return;
    }

    // MSAL v5: ACQUIRE_TOKEN_SUCCESS fires on both login and silent refresh
    if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && (event.payload as AuthenticationResult).account) {
      const result = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(result.account);

      if (pendingActivityLogin) {
        pendingActivityLogin = false;
        ActivityLogin(result.accessToken);
      }
    }
  });

  const redirectResult = await msalInstance.handleRedirectPromise();

  if (redirectResult?.account) {
    msalInstance.setActiveAccount(redirectResult.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <Suspense fallback={<AppLoading />}>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </Suspense>
    </StrictMode>,
  );
});
